import type { MatrixPos, Priority, Task, TaskPatch } from "@app/core";
import { effectivePriorityOf, matrixCellCenter, matrixEnabledFor, matrixIndexOf, matrixRowForPriority } from "@app/core";
import { useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import type { I18nKey } from "../../lib/language.js";
import { useDismiss } from "../../lib/useDismiss.js";
import { useI18n } from "../../lib/useI18n.js";
import { useRecurringConfirmStore, useSettingsStore, useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { useEffectiveMatrixDisplay, useEffectivePriorityCtx } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { SnoozeMenu } from "../snooze/index.js";
import { ColorSwatchGrid } from "../ColorSwatchGrid.js";
import { PRIORITY_PAPER } from "../task-form/paper/palette.js";
import { occurrenceRefOf } from "../../lib/virtual-id.js";
import { useRecurringTaskActions } from "./useRecurringTaskActions.js";
import { TaskDebugDialog } from "./TaskDebugDialog.js";

interface Props {
  task: Task;
  x: number;
  y: number;
  onClose: () => void;
  onDelete?: () => void;
}

export function TaskContextMenu({ task, x, y, onClose, onDelete }: Props) {
  const { t } = useI18n();
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const snooze = useTasksStore((s) => s.snooze);
  const unsnooze = useTasksStore((s) => s.unsnooze);
  const snoozeOccurrence = useTasksStore((s) => s.snoozeOccurrence);
  const remove = useTasksStore((s) => s.remove);
  const duplicate = useTasksStore((s) => s.duplicate);
  const edit = useTasksStore((s) => s.edit);
  const detachWithPatch = useTasksStore((s) => s.detachWithPatch);
  const editSeriesFromOccurrence = useTasksStore((s) => s.editSeriesFromOccurrence);
  const setColor = useTasksStore((s) => s.setColor);
  const setPriority = useTasksStore((s) => s.setPriority);
  const nestUnder = useTasksStore((s) => s.nestUnder);
  const askRecurring = useRecurringConfirmStore((s) => s.ask);
  // A standalone (scheduled) subtask can be sent back inside its parent block.
  const canReturnToParent = !task.virtual && !!task.subtask_parent_id && !!task.due_at;
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const actions = useRecurringTaskActions();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [snoozing, setSnoozing] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const done = task.status === "done";
  const snoozed = task.status === "snoozed";
  const canSnooze = !task.is_event;
  // Matrix in scope ⇒ priority is DERIVED from matrix_pos and the flag is
  // read-only everywhere; the rail becomes importance + urgency flags that
  // write the matrix position instead of the (snapshot) priority column.
  const matrixInScope = useSettingsStore((s) => matrixEnabledFor(s.matrixScope, task.is_event));
  const matrixDisplayOf = useEffectiveMatrixDisplay();
  const priorityCtx = useEffectivePriorityCtx();
  const matrixDisplay = matrixInScope ? matrixDisplayOf(task) : null;

  const style: React.CSSProperties = {
    position: "fixed",
    left: Math.min(x, window.innerWidth - 200),
    top: Math.max(12, Math.min(y, window.innerHeight - 300)),
    zIndex: 9999,
  };

  useDismiss(onClose, { capture: true, enabled: !showDebug && !snoozing }, menuRef);

  async function handleComplete() {
    onClose();
    // Virtual occurrences use the original expansion ISO (from the virtual ID),
    // not task.due_at which may be overridden by a single reschedule override.
    if (task.virtual && task.parent_id) {
      const occIso = occurrenceRefOf(task);
      await completeOccurrence(task.parent_id, occIso, !done);
      return;
    }
    await complete(task.id, !done);
  }

  function handleSnoozePick(atIso: string) {
    if (task.virtual && task.parent_id) {
      const occIso = occurrenceRefOf(task);
      void snoozeOccurrence(task.parent_id, occIso, atIso);
      return;
    }
    void snooze(task.id, atIso);
  }

  async function handleWake() {
    onClose();
    // Waking an occurrence sets its override back to "open" (same path as
    // reopening a done occurrence); a real task clears its snooze.
    if (task.virtual && task.parent_id) {
      const occIso = occurrenceRefOf(task);
      await completeOccurrence(task.parent_id, occIso, false);
      return;
    }
    await unsnooze(task.id);
  }

  async function handleDelete() {
    onClose();

    if (task.recurrence_id) {
      const isVirtual = !!task.virtual;
      const sourceId = isVirtual ? (task.parent_id ?? task.id) : task.id;
      // Use the original expansion ISO from the virtual ID, not task.due_at,
      // so deletions correctly target moved occurrences.
      const occurrenceIso = isVirtual
        ? occurrenceRefOf(task)
        : (task.due_at ?? new Date().toISOString());
      await actions.removeTask({ task, sourceId, occurrenceIso, isVirtual });
      return;
    }

    if (onDelete) onDelete();
    else await remove(task.id);
  }

  function handleCreate() {
    onClose();
    const d = task.due_at ? new Date(task.due_at) : new Date();
    openCreate(d, task.all_day ? 1440 : undefined);
  }

  function handleColor(color: string | null) {
    // Virtual tasks aren't in the store; resolve to the source row before
    // asking which part of the series should receive the property change.
    const targetId = task.virtual && task.parent_id ? task.parent_id : task.id;
    flushSync(onClose);
    afterNextPaint(() => {
      void applyPropertyPatch(targetId, { custom_color: color });
    });
  }

  function handlePriority(priority: Priority) {
    const targetId = task.virtual && task.parent_id ? task.parent_id : task.id;
    flushSync(onClose);
    afterNextPaint(() => {
      void applyPropertyPatch(targetId, { priority });
    });
  }

  function handleMatrixAxis(axis: "importance" | "urgency", level: number) {
    const targetId = task.virtual && task.parent_id ? task.parent_id : task.id;
    flushSync(onClose);
    afterNextPaint(() => {
      void applyMatrixAxis(targetId, axis, level);
    });
  }

  // matrix_pos is a whole-series scalar (like `location`): it blanket-applies
  // to the series base, so no single/following/all prompt here. The persisted
  // `priority` is refreshed to the new diagonal so snapshot consumers
  // (backup/sync/notifications) stay in step with what the matrix renders.
  async function applyMatrixAxis(targetId: string, axis: "importance" | "urgency", level: number) {
    const source = useTasksStore.getState().tasks.find((t) => t.id === targetId) ?? task;
    const center = matrixCellCenter(level);
    const pos = source.matrix_pos;
    let next: MatrixPos;
    if (pos) {
      next = axis === "importance"
        ? { ...pos, importance: center }
        : { ...pos, urgency: center, urgencyAuto: false };
    } else if (axis === "importance") {
      // First placement via an importance flag leaves urgency on auto, so the
      // deadline keeps driving the other axis (same as an unplaced task).
      next = { urgency: 0.5, importance: center, urgencyAuto: true };
    } else {
      const row = matrixRowForPriority(source.priority);
      next = { urgency: center, importance: row === null ? 0.5 : matrixCellCenter(row), urgencyAuto: false };
    }
    const priority = effectivePriorityOf({ ...source, matrix_pos: next }, priorityCtx);
    await edit(source.id, { matrix_pos: next, priority });
  }

  async function applyPropertyPatch(targetId: string, patch: Pick<TaskPatch, "custom_color" | "priority">) {
    const source = useTasksStore.getState().tasks.find((t) => t.id === targetId) ?? task;
    if (!source.recurrence_id) {
      if (patch.custom_color !== undefined) await setColor(targetId, patch.custom_color);
      if (patch.priority !== undefined) await setPriority(targetId, patch.priority);
      return;
    }

    const occurrenceIso = task.virtual
      ? occurrenceRefOf(task)
      : (task.due_at ?? source.due_at ?? new Date().toISOString());
    const choice = await askRecurring({
      kind: "edit",
      task,
      sourceId: source.id,
    });
    if (choice === "cancel") return;
    if (choice === "single") {
      await detachWithPatch(source.id, occurrenceIso, {
        due_at: task.due_at ?? occurrenceIso,
        ...patch,
      });
      return;
    }
    if (choice === "series") {
      await editSeriesFromOccurrence(source.id, occurrenceIso, patch);
      return;
    }
    await edit(source.id, patch);
  }

  if (snoozing) {
    return <SnoozeMenu x={x} y={y} onPick={handleSnoozePick} onClose={onClose} />;
  }

  if (showDebug) {
    return <TaskDebugDialog task={task} onClose={onClose} />;
  }

  return createPortal(
    <div
      ref={menuRef}
      style={style}
      className="relative w-48 overflow-visible text-card-foreground"
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {matrixInScope ? (
        <>
          {/* Importance flags (matrix rows) hang off the RIGHT SIDE of the top edge… */}
          <div
            className="absolute -top-3 left-3 right-3 z-0 flex h-7 items-start justify-end gap-1 overflow-visible"
            aria-label={t("matrix.importance")}
            role="radiogroup"
          >
            {AXIS_OPTIONS.map((p, i) => (
              <FlagButton
                key={p.id}
                color={p.color}
                name={t("matrix.axisOption", { axis: t("matrix.importance"), level: t(PRIORITY_LABEL_KEYS[p.id]) })}
                selected={matrixDisplay ? matrixIndexOf(matrixDisplay.importance) === i : false}
                onClick={() => handleMatrixAxis("importance", i)}
              />
            ))}
          </div>
          {/* …and urgency flags (matrix columns) off the TOP of the right edge.
              The row is rotated 90° about its top-left corner, so the flags'
              "lift" transforms point rightward, out of the menu. */}
          <div
            className="absolute left-[calc(100%+0.75rem)] top-4 z-0 flex h-7 origin-top-left rotate-90 items-start gap-1 overflow-visible"
            aria-label={t("matrix.urgency")}
            role="radiogroup"
          >
            {AXIS_OPTIONS.map((p, i) => (
              <FlagButton
                key={p.id}
                color={p.color}
                name={t("matrix.axisOption", { axis: t("matrix.urgency"), level: t(PRIORITY_LABEL_KEYS[p.id]) })}
                selected={matrixDisplay ? matrixIndexOf(matrixDisplay.urgency) === i : false}
                onClick={() => handleMatrixAxis("urgency", i)}
              />
            ))}
          </div>
        </>
      ) : (
        <div
          className="absolute -top-3 left-3 right-3 z-0 flex h-7 items-start gap-1 overflow-visible"
          aria-label={t("sidebar.priority")}
          role="radiogroup"
        >
          {PRIORITY_OPTIONS.map((p) => (
            <FlagButton
              key={p.id}
              color={p.color}
              name={t(PRIORITY_LABEL_KEYS[p.id])}
              selected={task.priority === p.id}
              empty={p.id === "none"}
              onClick={() => handlePriority(p.id)}
            />
          ))}
        </div>
      )}
      <div className="relative z-10 flex flex-col rounded-md border bg-card/85 py-1 shadow-lg backdrop-blur-sm">
        {matrixInScope && (
          <>
            {/* Axis captions INSIDE the popup: under the top (importance) flags
                and left of the right-edge (urgency) flags, each centered on its
                rail's MIDDLE flag (flags are w-5 + gap-1 from the edge inset).
                Non-interactive; they float over the item rows. */}
            <span className="pointer-events-none absolute right-[46px] top-1 translate-x-1/2 text-[calc(9px*var(--app-text-scale))] font-medium leading-none tracking-wide text-muted-foreground">
              {t("matrix.importance")}
            </span>
            <span className="pointer-events-none absolute right-1 top-[50px] -translate-y-1/2 text-[calc(9px*var(--app-text-scale))] font-medium tracking-wide text-muted-foreground [writing-mode:vertical-rl]">
              {t("matrix.urgency")}
            </span>
          </>
        )}
        <button
          onClick={() => void handleComplete()}
          className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
        >
          {t(done ? "contextMenu.markUncomplete" : "contextMenu.markComplete")}
        </button>
        {canSnooze && (
          snoozed ? (
            <button
              onClick={() => void handleWake()}
              className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
            >
              {t("contextMenu.wakeNow")}
            </button>
          ) : (
            <button
              onClick={() => setSnoozing(true)}
              className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
            >
              {t("contextMenu.snooze")}
            </button>
          )
        )}
        <button
          onClick={handleCreate}
          className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
        >
          {t("contextMenu.createTaskHere")}
        </button>
        <button
          onClick={() => { onClose(); void duplicate(task); }}
          className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
        >
          {t(task.is_event ? "contextMenu.duplicateEvent" : "contextMenu.duplicateTask")}
        </button>
        {canReturnToParent && (
          <button
            onClick={() => { onClose(); void nestUnder(task.id, task.subtask_parent_id!); }}
            className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
          >
            {t("contextMenu.moveToParent")}
          </button>
        )}
        <button
          onClick={() => void handleDelete()}
          className="px-3 py-2 text-left text-sm text-destructive hover:bg-accent/80"
        >
          Delete
        </button>
        <div className="my-1 border-t" />
        <button
          onClick={() => setShowDebug(true)}
          className="px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent/80 hover:text-accent-foreground"
        >
          Debug information
        </button>
        <div className="my-1 border-t" />
        <div className="px-3 pb-2 pt-1">
          <p className="mb-1.5 text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">{t("common.color")}</p>
          <ColorSwatchGrid onSelect={(v) => void handleColor(v)} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

const PRIORITY_OPTIONS: Array<{ id: Priority; name: string; color: string }> = PRIORITY_PAPER;
/** Low/medium/high (no "none") — one flag per matrix third, in axis order. */
const AXIS_OPTIONS = PRIORITY_PAPER.filter((p) => p.id !== "none");
const PRIORITY_LABEL_KEYS: Record<Priority, I18nKey> = {
  high: "sidebar.priority.high",
  medium: "sidebar.priority.medium",
  low: "sidebar.priority.low",
  none: "sidebar.priority.none",
};

interface FlagButtonProps {
  color: string;
  name: string;
  selected: boolean;
  empty?: boolean;
  onClick: () => void;
}

function FlagButton({ color, name, selected, empty = false, onClick }: FlagButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        // Selected vs idle classes are mutually exclusive — a shared
        // `translate-y-1`/`h-6` base would WIN the Tailwind specificity tie
        // against `-translate-y-3`/`h-8`, leaving the selected flag unlifted
        // (it only grew downward, hidden under the menu body).
        "flex w-5 shrink-0 items-start justify-center overflow-hidden border border-b-0 px-0 text-[calc(10px*var(--app-text-scale))] font-medium tracking-wide transition-[filter,transform,width,height] duration-300",
        selected && !empty
          ? "h-8 -translate-y-3 pt-0.5 shadow-none [clip-path:polygon(0_0,100%_0,100%_100%,92%_88%,84%_100%,76%_90%,68%_100%,60%_89%,52%_100%,44%_88%,36%_100%,28%_90%,20%_100%,12%_89%,4%_100%,0_92%)]"
          : "h-6 translate-y-1 pt-1 shadow-sm hover:-translate-y-0.5 hover:drop-shadow-md active:-translate-y-1.5",
      ].join(" ")}
      style={{
        backgroundColor: color,
        borderColor: `color-mix(in oklch, ${color} 55%, currentColor 25%)`,
        color: `color-mix(in oklch, ${color} 30%, currentColor 90%)`,
      }}
      title={name}
      aria-checked={selected}
      role="radio"
    >
      {selected && !empty && (
        <span className="relative h-3.5 w-3 flex-none" aria-hidden="true">
          <span
            className="absolute bottom-0 left-px top-0 w-px"
            style={{ backgroundColor: `color-mix(in oklch, ${color} 40%, currentColor 70%)` }}
          />
          <span
            className="absolute left-[3px] top-px h-2 w-2.5 [clip-path:polygon(0_0,100%_0,75%_50%,100%_100%,0_100%)]"
            style={{ backgroundColor: `color-mix(in oklch, ${color} 50%, currentColor 50%)` }}
          />
        </span>
      )}
    </button>
  );
}

function afterNextPaint(fn: () => void) {
  requestAnimationFrame(() => {
    window.setTimeout(fn, 0);
  });
}
